import React from "react";
import {
  FaUserTie,
  FaIdCard,
  FaCalendarAlt,
  FaPhone,
  FaEnvelope,
  FaBuilding,
  FaBook,
  FaMoneyBillWave,
  FaUniversity,
  FaGraduationCap,
  FaFilePdf,
  FaClock,
  FaCheckCircle,
  FaTimesCircle,
  FaMapMarkerAlt,
  FaUser,
  FaBriefcase,
  FaCreditCard,
  FaLayerGroup,
} from "react-icons/fa";

export default function EmployeeDetails({ employee, onClose }) {
  if (!employee) return null;

  const parseQualifications = (quals) => {
    if (!quals) return [];
    if (Array.isArray(quals)) return quals;
    if (typeof quals === "string") {
      try {
        return JSON.parse(quals);
      } catch (e) {
        return [];
      }
    }
    return [];
  };

  const qualificationsList = parseQualifications(
    employee.qualifications || employee.Qualifications,
  );

  const DetailItem = ({
    icon: Icon,
    label,
    value,
    highlight = false,
    isLink = false,
    badge = false,
    badgeColor = "bg-slate-200/80 text-slate-800 border-slate-300 dark:bg-slate-700 dark:text-slate-200 dark:border-slate-600",
  }) => (
    <div
      className={`flex items-start p-3 rounded-xl transition-all ${
        highlight
          ? "bg-slate-100 dark:bg-slate-800/80 border border-slate-300 dark:border-slate-700"
          : "bg-slate-50 dark:bg-slate-800/40 border border-slate-200/70 dark:border-slate-800"
      }`}
    >
      <div
        className={`mt-0.5 mr-3 p-2 rounded-lg flex-shrink-0 ${
          highlight
            ? "bg-slate-700 dark:bg-slate-600 text-white"
            : "bg-slate-200 dark:bg-slate-700 text-slate-600 dark:text-slate-300"
        }`}
      >
        <Icon className="text-xs" />
      </div>
      <div className="flex-1 min-w-0">
        <p className="text-[10px] font-black uppercase tracking-wider text-slate-400 dark:text-slate-500 mb-0.5">
          {label}
        </p>
        {isLink && value ? (
          <a
            href={value}
            target="_blank"
            rel="noopener noreferrer"
            className="inline-flex items-center gap-1.5 text-xs font-bold text-slate-700 dark:text-slate-300 hover:text-slate-900 dark:hover:text-white underline"
          >
            <FaFilePdf className="text-red-500 text-xs" /> View Document
          </a>
        ) : badge ? (
          <span
            className={`inline-block px-2 py-0.5 rounded-md text-xs font-black border ${badgeColor}`}
          >
            {value || "—"}
          </span>
        ) : (
          <p
            className={`text-xs font-bold ${
              highlight
                ? "text-slate-900 dark:text-white font-mono"
                : "text-slate-800 dark:text-slate-100"
            } truncate`}
          >
            {value !== undefined && value !== null && value !== ""
              ? String(value)
              : "—"}
          </p>
        )}
      </div>
    </div>
  );

  return (
    <div className="space-y-5 text-slate-800 dark:text-slate-100 font-sans">
      {/* Top Profile Header Bar - Clean Light Gray (No Gradients or Blues) */}
      <div className="rounded-2xl bg-slate-100 dark:bg-slate-800/90 p-4 border border-slate-200 dark:border-slate-700 shadow-xs flex flex-col sm:flex-row items-center sm:items-start gap-4">
        <div className="relative flex-shrink-0">
          <img
            src={
              employee.profilePictureUrl ||
              employee.photoUrl ||
              `https://ui-avatars.com/api/?name=${encodeURIComponent(
                employee.name || "Employee",
              )}&background=e2e8f0&color=334155&bold=true`
            }
            alt={employee.name}
            className="w-16 h-16 rounded-xl object-cover border border-slate-300 dark:border-slate-600 shadow-2xs bg-slate-200"
          />
          <div
            className={`absolute -bottom-1 -right-1 p-0.5 rounded-full text-white text-[9px] shadow-xs ${
              (employee.isActive ?? true) ? "bg-emerald-600" : "bg-rose-600"
            }`}
            title={
              (employee.isActive ?? true) ? "Active Staff" : "Inactive Staff"
            }
          >
            {(employee.isActive ?? true) ? (
              <FaCheckCircle />
            ) : (
              <FaTimesCircle />
            )}
          </div>
        </div>

        <div className="text-center sm:text-left flex-1 min-w-0">
          <div className="flex flex-wrap items-center justify-center sm:justify-start gap-2 mb-1">
            <h2 className="text-lg font-black tracking-tight text-slate-900 dark:text-white">
              {employee.name || "Staff Member"}
            </h2>
            <span className="bg-slate-200 dark:bg-slate-700 text-slate-800 dark:text-slate-200 text-[11px] font-mono font-black px-2 py-0.5 rounded-md border border-slate-300 dark:border-slate-600">
              {employee.employeeCode || employee.EmployeeCode || "NO-CODE"}
            </span>
            <span
              className={`text-[10px] font-black uppercase px-2 py-0.5 rounded-md ${
                (employee.isActive ?? true)
                  ? "bg-emerald-100 text-emerald-800 border border-emerald-300 dark:bg-emerald-950 dark:text-emerald-300 dark:border-emerald-800"
                  : "bg-rose-100 text-rose-800 border border-rose-300 dark:bg-rose-950 dark:text-rose-300 dark:border-rose-800"
              }`}
            >
              {(employee.isActive ?? true) ? "Active" : "Inactive"}
            </span>
          </div>

          <p className="text-xs text-slate-600 dark:text-slate-400 font-semibold mb-2">
            {employee.designation || "Staff Member"} •{" "}
            {employee.departmentName ||
              employee.department?.name ||
              "No Department"}
          </p>

          <div className="flex flex-wrap items-center justify-center sm:justify-start gap-2 text-xs">
            {employee.subjectName && (
              <span className="bg-white dark:bg-slate-900 border border-slate-200 dark:border-slate-700 px-2 py-0.5 rounded-md text-slate-700 dark:text-slate-300 font-medium flex items-center gap-1">
                <FaBook className="text-slate-400 text-[10px]" />{" "}
                {employee.subjectName}
              </span>
            )}
            {employee.section && (
              <span className="bg-white dark:bg-slate-900 border border-slate-200 dark:border-slate-700 px-2 py-0.5 rounded-md text-slate-700 dark:text-slate-300 font-medium flex items-center gap-1">
                <FaLayerGroup className="text-slate-400 text-[10px]" />{" "}
                {employee.section}
              </span>
            )}
            {employee.joiningDate && (
              <span className="bg-white dark:bg-slate-900 border border-slate-200 dark:border-slate-700 px-2 py-0.5 rounded-md text-slate-700 dark:text-slate-300 font-medium flex items-center gap-1">
                <FaCalendarAlt className="text-slate-400 text-[10px]" /> Joined:{" "}
                {employee.joiningDate}
              </span>
            )}
          </div>
        </div>
      </div>

      {/* 1. Personal & Contact Information */}
      <div>
        <h3 className="text-xs font-black text-slate-500 dark:text-slate-400 uppercase tracking-wider mb-2 flex items-center gap-1.5">
          <FaUser className="text-slate-400" /> Personal & Contact Details
        </h3>
        <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 gap-2">
          <DetailItem
            icon={FaUserTie}
            label="Full Name"
            value={employee.name}
          />
          <DetailItem
            icon={FaUser}
            label="Father's Name"
            value={employee.fatherName || employee.FatherName}
          />
          <DetailItem
            icon={FaUser}
            label="Mother's Name"
            value={employee.motherName || employee.MotherName}
          />
          <DetailItem
            icon={FaUser}
            label="Gender"
            value={employee.gender || employee.Gender}
          />
          <DetailItem
            icon={FaPhone}
            label="Mobile No"
            value={employee.mobileNo || employee.MobileNo}
            highlight
          />
          <DetailItem
            icon={FaEnvelope}
            label="Email Address"
            value={employee.email || employee.Email}
          />
          <div className="sm:col-span-2 md:col-span-3">
            <DetailItem
              icon={FaMapMarkerAlt}
              label="Residential Address"
              value={employee.address || employee.Address}
            />
          </div>
        </div>
      </div>

      {/* 2. Employment & Service Details */}
      <div>
        <h3 className="text-xs font-black text-slate-500 dark:text-slate-400 uppercase tracking-wider mb-2 flex items-center gap-1.5">
          <FaBriefcase className="text-slate-400" /> Employment & Service
          Details
        </h3>
        <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 gap-2">
          <DetailItem
            icon={FaBuilding}
            label="Department"
            value={employee.departmentName || employee.department?.name}
            badge
          />
          <DetailItem
            icon={FaUserTie}
            label="Designation"
            value={employee.designation}
            badge
          />
          <DetailItem
            icon={FaBook}
            label="Core Subject"
            value={employee.subjectName || employee.subject?.name}
          />
          <DetailItem
            icon={FaLayerGroup}
            label="School Section"
            value={employee.section}
          />
          <DetailItem
            icon={FaClock}
            label="Working Hours"
            value={employee.workingHours || employee.WorkingHours}
          />
          <DetailItem
            icon={FaCalendarAlt}
            label="Joining Date"
            value={employee.joiningDate}
          />
          <DetailItem
            icon={FaCalendarAlt}
            label="Salary Date"
            value={employee.salaryDate}
          />
          <DetailItem
            icon={FaMoneyBillWave}
            label="Monthly Salary"
            value={employee.salary ? `₹ ${employee.salary}` : "—"}
            highlight
          />
          <DetailItem
            icon={FaBriefcase}
            label="Experience"
            value={
              employee.experienceYears !== undefined
                ? `${employee.experienceYears} Years`
                : "—"
            }
          />
          <DetailItem
            icon={FaFilePdf}
            label="Resume / CV"
            value={employee.resumeFileUrl || employee.ResumeFileUrl}
            isLink
          />
        </div>
      </div>

      {/* 3. Identity Documents & Financial Records */}
      <div>
        <h3 className="text-xs font-black text-slate-500 dark:text-slate-400 uppercase tracking-wider mb-2 flex items-center gap-1.5">
          <FaCreditCard className="text-slate-400" /> Identity Documents & Bank
          Records
        </h3>
        <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 gap-2">
          <DetailItem
            icon={FaIdCard}
            label="Aadhaar Number"
            value={employee.aadhaarNo || employee.AadhaarNo}
          />
          <DetailItem
            icon={FaFilePdf}
            label="Aadhaar Card"
            value={employee.aadhaarFile || employee.AadhaarFile}
            isLink
          />
          <DetailItem
            icon={FaIdCard}
            label="PAN Number"
            value={employee.panNo || employee.PANNo || employee.panNumber}
            highlight
          />
          <DetailItem
            icon={FaFilePdf}
            label="PAN Card"
            value={employee.panFile || employee.PANFile}
            isLink
          />
          <DetailItem
            icon={FaUniversity}
            label="Bank Name"
            value={employee.bankName || employee.BankName}
          />
          <DetailItem
            icon={FaCreditCard}
            label="Bank Account No"
            value={employee.bankAccountNo || employee.BankAccountNo}
          />
          <DetailItem
            icon={FaUniversity}
            label="IFSC Code"
            value={employee.ifscCode || employee.IFSCCode}
          />
          <DetailItem
            icon={FaFilePdf}
            label="Passbook / Cheque"
            value={employee.passbookFileUrl || employee.PassbookFileUrl}
            isLink
          />
        </div>
      </div>

      {/* 4. Educational Qualifications */}
      <div>
        <h3 className="text-xs font-black text-slate-500 dark:text-slate-400 uppercase tracking-wider mb-2 flex items-center gap-1.5">
          <FaGraduationCap className="text-slate-400" /> Educational
          Qualifications ({qualificationsList.length})
        </h3>
        {qualificationsList.length === 0 ? (
          <div className="p-3 rounded-xl border border-slate-200 dark:border-slate-800 bg-slate-50 dark:bg-slate-800/40 text-center text-xs text-slate-400 font-medium">
            No qualification records attached.
          </div>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 gap-2">
            {qualificationsList.map((q, idx) => (
              <div
                key={idx}
                className="p-3 rounded-xl border border-slate-200 dark:border-slate-700 bg-white dark:bg-slate-800 shadow-2xs flex flex-col justify-between gap-2"
              >
                <div className="flex items-start justify-between">
                  <div>
                    <h4 className="text-xs font-black text-slate-900 dark:text-white">
                      {q.Degree || q.degree || "Degree"}
                    </h4>
                    <p className="text-[11px] font-semibold text-slate-500 dark:text-slate-400">
                      {q.Institute || q.institute || "University / Board"}
                    </p>
                  </div>
                  <span className="bg-slate-100 dark:bg-slate-700 text-slate-800 dark:text-slate-200 text-[10px] font-black px-2 py-0.5 rounded-md border border-slate-200 dark:border-slate-600">
                    {q.Year || q.year || "Year"}
                  </span>
                </div>
                <div className="flex items-center justify-between pt-2 border-t border-slate-100 dark:border-slate-700/60 text-[11px]">
                  <span className="text-slate-500 font-medium">
                    Score / Marks:
                  </span>
                  <span className="font-extrabold text-slate-900 dark:text-white">
                    {q.Percentage || q.percentage
                      ? `${q.Percentage || q.percentage}%`
                      : "—"}
                  </span>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}
